import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';

import { FormInputVariants, horizontalDirection } from '#/service/constants';
import { noop } from '#/service/utils';

import {
  Container,
  InputContainer,
  InputLabel,
  StyledIcon,
  StyledInput,
} from '../FormInput.styled';
import { CommonInputTextProps } from '../FormInput.types';
import { ErrorMessage } from './ErrorMessage';
import Markdown from './Markdown';

const InputText: FC<CommonInputTextProps> = ({
  dataKey,
  description,
  errorMessages,
  handleBlur = noop,
  handleFocus = noop,
  handleMouseEnter = noop,
  handleMouseLeave = noop,
  handleValidate = noop,
  highlighted,
  icon,
  inputValue,
  isFocused,
  label,
  required,
  updateInputValue = noop,
  touched,
  variant,
  help,
  groupKey = '',
  format,
  disabled,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isNumber = variant === FormInputVariants.NUMBER;
  const prevRequired = useRef<boolean>(!!required);

  const validate = useCallback(
    (value?: string) => {
      handleValidate(dataKey, groupKey, inputRef, value);
    },
    [dataKey, groupKey, handleValidate]
  );

  // re-validate when `required` changes
  useEffect(() => {
    if (!(touched && prevRequired.current === required)) {
      return;
    }
    inputRef.current?.setCustomValidity('');
    validate();
  }, [required, touched, validate]);

  const handleFocusWithRef = () => {
    handleFocus();
    inputRef.current?.focus();
  };

  const blurInput = () => {
    handleBlur();
    validate();
  };

  const togglePasswordVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    updateInputValue(dataKey, { value });
    inputRef?.current?.setCustomValidity('');
    validate(value);
  };

  const handleFocusConditionally = () => {
    if (!isFocused) {
      handleFocusWithRef();
    }
  };

  useEffect(() => {
    if (errorMessages) {
      errorMessages.forEach((errorMessage) =>
        inputRef.current?.setCustomValidity(errorMessage)
      );
    }
  }, [errorMessages]);

  const props = isNumber
    ? {
        getInputRef: inputRef,
        format,
        disabled,
      }
    : {
        ref: inputRef,
        disabled,
      };

  return (
    <Container>
      <InputContainer
        onClick={handleFocusConditionally}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <InputLabel
          $bigLabel={!isFocused && !inputValue}
          $hasIcon={!!icon}
          $highlighted={highlighted}
          htmlFor={`${groupKey}-${dataKey}`}
        >
          {label}
        </InputLabel>
        {icon && (
          <StyledIcon
            $highlighted={highlighted}
            aria-hidden="true"
            name={icon}
            position={horizontalDirection.LEFT}
          />
        )}
        <StyledInput
          $hasIcon={!!icon}
          $highlighted={highlighted}
          $touched={touched}
          as={isNumber ? NumberFormat : 'input'}
          id={`${groupKey}-${dataKey}`}
          name={dataKey}
          onBlur={blurInput}
          onChange={handleChange}
          onFocus={handleFocusWithRef}
          required={required}
          type={
            isPasswordVisible || isNumber ? FormInputVariants.TEXT : variant
          }
          value={inputValue || ''}
          {...props}
        />
        {variant === FormInputVariants.PASSWORD && (
          <StyledIcon
            $highlighted={highlighted}
            name={isPasswordVisible ? 'eye' : 'eye_slash'}
            onMouseDown={togglePasswordVisibility}
            position={horizontalDirection.RIGHT}
          />
        )}
        {help && (
          <>
            <StyledIcon
              aria-hidden="true"
              data-for={`help-${dataKey}`}
              data-tip
              name="questionmark_circle"
              position={horizontalDirection.RIGHT}
            />
            {help}
          </>
        )}
      </InputContainer>
      {description && <Markdown>{description}</Markdown>}
      {errorMessages?.map((errorMessage) => (
        <ErrorMessage key={errorMessage}>{errorMessage}</ErrorMessage>
      ))}
    </Container>
  );
};

export default InputText;
